import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconButton } from "@mui/material";
import {
  FlagOutlined,
  InfoOutlined,
  ModeEditOutlineTwoTone,
} from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import CustomTabs from "components/Shared/CustomTabs";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyGetCountriesQuery } from "store/services/countryApi";

import { useAppMeta, useTablePagination } from "hooks";

export const CountryListScreen = () => {
  const { page, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Countries" }];

  const countryTabs = [
    {
      label: "Active",
      value: "Active",
    },
    {
      label: "Inactive",
      value: "Inactive",
    },
  ];

  const columns = [
    {
      name: "",
      cell: (row) =>
        !row?.flagUrl?.png ? (
          <FlagOutlined />
        ) : (
          <img
            src={row?.flagUrl?.png}
            title={row?.flagUrl?.png}
            alt="Flag"
            className="my-1.5 h-8 w-full rounded-sm object-contain"
          />
        ),
      width: "80px",
    },
    {
      name: "Country",
      selector: (row) => row?.name,
      cell: (row) => (
        <Link
          to={`/app/crm/country/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          <HighlightedText text={row?.name} highlight={keyword} />
        </Link>
      ),
    },
    {
      name: "CCA2",
      selector: (row) => row?.cca2,
      cell: (row) => <HighlightedText text={row?.cca2} highlight={keyword} />,
    },
    {
      name: "CCA3",
      selector: (row) => row?.cca3,
      cell: (row) => <HighlightedText text={row?.cca3} highlight={keyword} />,
    },
    {
      name: "Currency",
      selector: (row) => row?.currency?.code,
    },
    {
      name: "Region",
      selector: (row) => row?.region,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/country/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Country">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>

          {false && (
            <Link to={`/app/crm/country/${row._id}/edit`}>
              <IconButton size="small" aria-label="Edit Country">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )}
        </>
      ),
      maxWidth: "100px",
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchCountries,
    {
      data: countryRes,
      isLoading: loadingCountries,
      isFetching: fetchingCountries,
      error,
    },
  ] = useLazyGetCountriesQuery();

  React.useEffect(() => {
    fetchCountries({
      active: activeTab !== "Inactive",
      page,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, fetchCountries, keyword, page, perPage]);

  useAppMeta(`${activeTab} Countries`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={loadingCountries || fetchingCountries}
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={countryTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Name/CCA2/CCA3"
          disabled={loadingCountries || fetchingCountries}
        >
          <div className="font-medium"> {activeTab} Countries </div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={countryRes?.data ?? []}
          progressPending={loadingCountries || fetchingCountries}
          paginationServer
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          paginationTotalRows={countryRes?.totalItems ?? 0}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};
